import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import Navbar from "../../../../globals/components/Navbar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { apiClient } from "../../../../globals/utils/api";
import { toast } from "react-toastify";
import { NumericFormat } from "react-number-format";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value, // Ensure this is a string
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      fixedDecimalScale
    />
  );
});

function LawFirmUserLimits() {
  const { lawfirm_user_id } = useParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog States for Adding and Editing
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLimitId, setCurrentLimitId] = useState(null);
  const [singleLimit, setSingleLimit] = useState(0);
  const [aggregateLimit, setAggregateLimit] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchLimits = async () => {
      setLoading(true);
      try {
        const response = await apiClient(
          `/lawfirm-user/${lawfirm_user_id}/limits/`,
          "GET"
        );
        setLimits(response);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchLimits();
  }, [lawfirm_user_id]);

  const currentRows = limits.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const openAddDialog = () => {
    setSingleLimit(0);
    setAggregateLimit(0);
    setEffectiveDate("");
    setExpirationDate("");
    setIsEditing(false);
    setOpen(true);
  };

  const openEditDialog = (limit) => {
    setSingleLimit(limit.line_of_business_single_limit);
    setAggregateLimit(limit.line_of_business_aggregate_limit);
    setEffectiveDate(limit.letter_of_authority_effective_date);
    setExpirationDate(limit.letter_of_authority_expiration_date);
    setCurrentLimitId(limit.limit_id);
    setIsEditing(true);
    setOpen(true);
  };

  const handleSaveLimit = async () => {
    setLoading(true);
    try {
      const payload = {
        lawfirm_user: lawfirm_user_id,
        line_of_business_single_limit: singleLimit,
        line_of_business_aggregate_limit: aggregateLimit,
        letter_of_authority_effective_date: effectiveDate,
        letter_of_authority_expiration_date: expirationDate,
      };

      if (isEditing) {
        // Update an existing limit
        await apiClient(`/limits/${currentLimitId}/`, "PATCH", payload);
        toast.success("Limit updated successfully!");
      } else {
        // Create a new limit
        await apiClient(
          `/lawfirm-user/${lawfirm_user_id}/limits/`,
          "POST",
          payload
        );
        toast.success("Limit added successfully!");
      }

      setOpen(false);

      // Refresh the limits
      const response = await apiClient(
        `/lawfirm-user/${lawfirm_user_id}/limits/`,
        "GET"
      );
      setLimits(response);
    } catch (error) {
      toast.error("Failed to save limit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLimit = async (limitId) => {
    setLoading(true);
    try {
      await apiClient(`/limits/${limitId}/`, "DELETE");
      toast.success("Limit deleted successfully!");

      // Refresh the limits
      const response = await apiClient(
        `/lawfirm-user/${lawfirm_user_id}/limits/`,
        "GET"
      );
      setLimits(response);
    } catch (error) {
      toast.error("Failed to delete limit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  function formatToUSDWithoutCents(amount) {
    if (!amount) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  }

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "100%", mt: 4, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Law Firm User Limits
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            Add Limit
          </Button>
        </Box>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell align="left">Effective Date</TableCell>
                  <TableCell align="left">Expiration Date</TableCell>
                  <TableCell align="right">Single Limit</TableCell>
                  <TableCell align="right">Aggregate Limit</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((limit) => (
                  <TableRow key={limit.limit_id}>
                    <TableCell align="left">
                      {formatDate(limit.letter_of_authority_effective_date)}
                    </TableCell>
                    <TableCell align="left">
                      {formatDate(limit.letter_of_authority_expiration_date)}
                    </TableCell>
                    <TableCell align="right">
                      {formatToUSDWithoutCents(
                        limit.line_of_business_single_limit
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatToUSDWithoutCents(
                        limit.line_of_business_aggregate_limit
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => openEditDialog(limit)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLimit(limit.limit_id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={limits.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      {/* Dialog for Adding/Editing Limits */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{isEditing ? "Edit Limit" : "Add Limit"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Single Limit"
            fullWidth
            value={singleLimit.toString()}
            onChange={(e) => setSingleLimit(parseFloat(e.target.value) || 0)}
            InputProps={{
              inputComponent: NumberFormatCustom,
            }}
          />
          <TextField
            margin="dense"
            label="Aggregate Limit"
            fullWidth
            value={aggregateLimit.toString()}
            onChange={(e) => setAggregateLimit(parseFloat(e.target.value) || 0)}
            InputProps={{
              inputComponent: NumberFormatCustom,
            }}
          />
          <TextField
            margin="dense"
            label="Effective Date"
            type="date"
            fullWidth
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Expiration Date"
            type="date"
            fullWidth
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveLimit} color="primary" variant="contained">
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LawFirmUserLimits;
